import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/authStore';
import { MessageSquare, Send, User } from 'lucide-react';

export const AdminMessagesPage: React.FC = () => {
  const { user } = useAuth();
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null);
  const [replyContent, setReplyContent] = useState('');

  const queryClient = useQueryClient();

  const { data: threadsRes, isLoading } = useQuery({
    queryKey: ['admin-messages'],
    queryFn: () => api.get('/admin/messages').then((res) => res.data.data),
  });

  const threads = threadsRes || [];
  const activeThread = threads.find((t: any) => t.id === selectedThreadId) || threads[0] || null;

  const replyMutation = useMutation({
    mutationFn: async ({ threadId, content }: { threadId: string; content: string }) => {
      return api.post('/admin/messages/reply', { threadId, content });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-messages'] });
      setReplyContent('');
    },
  });

  const handleSendReply = (e: React.FormEvent) => {
    e.preventDefault();
    if (!replyContent.trim() || !activeThread) return;
    replyMutation.mutate({
      threadId: activeThread.id,
      content: replyContent.trim(),
    });
  };

  return (
    <div className="space-y-8">
      
      <div>
        <h1 className="text-2xl sm:text-3xl font-serif font-bold text-white">
          Client Communication Center
        </h1>
        <p className="text-xs sm:text-sm text-slate-400">
          Direct threaded dialogue with authors and contracted publishing partners.
        </p>
      </div>

      {isLoading ? (
        <div className="p-12 text-center text-xs font-mono text-slate-400">Loading conversations...</div>
      ) : threads.length === 0 ? (
        <div className="p-12 rounded-2xl bg-slate-950 border border-slate-800 text-center space-y-3">
          <MessageSquare className="w-12 h-12 text-slate-600 mx-auto" />
          <h3 className="font-serif text-lg font-bold text-white">No Active Message Threads</h3>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 bg-slate-950 rounded-2xl border border-slate-800 overflow-hidden min-h-[600px] shadow-xl">
          
          {/* Threads Sidebar */}
          <div className="lg:col-span-4 border-r border-slate-800 p-4 space-y-2 bg-slate-900/40">
            <span className="text-[10px] font-mono uppercase tracking-wider text-slate-500 font-bold px-2 block">
              All Threads ({threads.length})
            </span>
            <div className="space-y-1">
              {threads.map((thr: any) => {
                const isSelected = activeThread?.id === thr.id;
                return (
                  <div
                    key={thr.id}
                    onClick={() => setSelectedThreadId(thr.id)}
                    className={`p-3.5 rounded-xl cursor-pointer transition-all ${
                      isSelected
                        ? 'bg-slate-850 border border-brand-500/50 shadow-xs'
                        : 'hover:bg-slate-900 border border-transparent'
                    }`}
                  >
                    <div className="flex items-center justify-between text-xs font-bold text-white truncate">
                      <span>{thr.title}</span>
                      <span className="text-[10px] font-mono text-slate-500 font-normal">
                        {new Date(thr.lastMessageAt).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="text-[11px] text-slate-400 mt-0.5">
                      Client: {thr.client?.firstName} {thr.client?.lastName}
                    </div>
                    {thr.project && (
                      <span className="text-[10px] font-mono text-brand-400 block mt-0.5">
                        [{thr.project.projectCode}] {thr.project.title}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Chat Stream */}
          <div className="lg:col-span-8 flex flex-col justify-between p-6">
            <div className="border-b border-slate-800 pb-4">
              <h3 className="font-serif text-lg font-bold text-white">{activeThread?.title}</h3>
              <p className="text-[11px] text-slate-400 font-mono">
                Client: {activeThread?.client?.firstName} {activeThread?.client?.lastName} ({activeThread?.client?.email})
              </p>
            </div>

            <div className="flex-1 overflow-y-auto py-6 space-y-4 max-h-[420px] pr-2">
              {activeThread?.messages?.map((msg: any) => {
                const isStaff = msg.senderId === user?.id;
                return (
                  <div
                    key={msg.id}
                    className={`flex flex-col ${isStaff ? 'items-end' : 'items-start'}`}
                  >
                    <div
                      className={`max-w-md p-4 rounded-2xl text-xs sm:text-sm leading-relaxed shadow-xs ${
                        isStaff
                          ? 'bg-brand-600 text-white rounded-br-none'
                          : 'bg-slate-900 text-slate-200 border border-slate-800 rounded-bl-none'
                      }`}
                    >
                      <span className={`text-[10px] font-bold block mb-1 font-mono ${isStaff ? 'text-brand-200' : 'text-brand-400'}`}>
                        {isStaff ? 'Editorial Staff (You)' : `${activeThread?.client?.firstName || 'Author'}`}
                      </span>
                      <p className="whitespace-pre-wrap">{msg.content}</p>
                    </div>
                    <span className="text-[10px] text-slate-500 font-mono mt-1 px-1">
                      {new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                );
              })}
            </div>

            <form onSubmit={handleSendReply} className="pt-4 border-t border-slate-800 flex items-center gap-2">
              <input
                type="text"
                value={replyContent}
                onChange={(e) => setReplyContent(e.target.value)}
                placeholder="Write an official editorial reply to the client..."
                className="flex-1 px-4 py-3 rounded-xl bg-slate-900 border border-slate-800 text-white text-xs focus:ring-2 focus:ring-brand-500 focus:outline-hidden"
              />
              <button
                type="submit"
                disabled={!replyContent.trim() || replyMutation.isPending}
                className="p-3 rounded-xl bg-brand-600 hover:bg-brand-500 text-white font-bold text-xs disabled:opacity-50 transition-colors shrink-0"
              >
                <Send className="w-4 h-4" />
              </button>
            </form>
          </div>

        </div>
      )}

    </div>
  );
};
